/**
 * Layer 2: DesignTokenSet
 *
 * Controls VISUAL styling - colors, typography, spacing, and cultural aesthetics.
 *
 * This layer determines the sensory experience, not the structural organization.
 */

export type DesignTokenSet =
  | 'elegant'              // Sophisticated neutrals with gold accents
  | 'japanese-zen'         // Minimalist earth tones inspired by Japanese aesthetics
  | 'scandinavian-hygge'   // Warm, cozy Nordic color palette
  | 'mediterranean-sun'    // Vibrant blues, terracotta, and warm whites

export const DESIGN_TOKEN_SETS: DesignTokenSet[] = [
  'elegant',
  'japanese-zen',
  'scandinavian-hygge',
  'mediterranean-sun',
]

/**
 * DS3: Color Mode (Dark Mode Architecture)
 *
 * Controls light vs dark mode for each design token set.
 * CSS generated with [data-visual="theme"][data-theme="dark"] selectors.
 */
export type ColorMode = 'light' | 'dark'

export const COLOR_MODES: ColorMode[] = ['light', 'dark']

export interface ColorPalette {
  primary: string
  secondary: string
  accent: string
  background: string
  surface: string
  text: {
    primary: string
    secondary: string
    disabled: string
  }
  semantic: {
    success: string
    warning: string
    error: string
    info: string
  }
}

export interface TypographyScale {
  fontFamily: {
    sans: string
    serif: string
    mono: string
  }
  fontSize: {
    xs: string
    sm: string
    base: string
    lg: string
    xl: string
    '2xl': string
    '3xl': string
    '4xl': string
  }
  fontWeight: {
    light: number
    normal: number
    medium: number
    semibold: number
    bold: number
  }
  lineHeight: {
    tight: number
    normal: number
    relaxed: number
  }
}

export interface SpacingScale {
  xs: string
  sm: string
  md: string
  lg: string
  xl: string
  '2xl': string
  '3xl': string
  '4xl': string
}

export interface DesignTokenSetConfig {
  id: DesignTokenSet
  name: string
  description: string
  culturalOrigin: string
  colors: ColorPalette
  typography: TypographyScale
  spacing: SpacingScale
  borderRadius: {
    none: string
    sm: string
    md: string
    lg: string
    full: string
  }
}

// Elegant Token Set (Default)
export const ELEGANT_TOKENS: DesignTokenSetConfig = {
  id: 'elegant',
  name: 'Elegant',
  description: 'Sophisticated neutrals with gold accents',
  culturalOrigin: 'Contemporary Western',
  colors: {
    primary: '#1a1a1a',
    secondary: '#4a4a4a',
    accent: '#d4af37',
    background: '#ffffff',
    surface: '#f5f5f5',
    text: {
      primary: '#1a1a1a',
      secondary: '#6b7280',
      disabled: '#9ca3af',
    },
    semantic: {
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
  },
  typography: {
    fontFamily: {
      sans: 'Inter, system-ui, -apple-system, sans-serif',
      serif: 'Merriweather, Georgia, serif',
      mono: 'JetBrains Mono, Menlo, monospace',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
}

// Japanese Zen Token Set
export const JAPANESE_ZEN_TOKENS: DesignTokenSetConfig = {
  id: 'japanese-zen',
  name: 'Japanese Zen',
  description: 'Minimalist earth tones inspired by Japanese aesthetics',
  culturalOrigin: 'Japanese Wabi-Sabi',
  colors: {
    primary: '#2d3436',        // Charcoal (sumi ink)
    secondary: '#636e72',      // Stone gray
    accent: '#6c7a89',         // Bamboo green-gray
    background: '#f5f4f0',     // Rice paper white
    surface: '#ffffff',        // Pure white
    text: {
      primary: '#2d3436',      // Charcoal
      secondary: '#636e72',    // Stone gray
      disabled: '#b2bec3',     // Light stone
    },
    semantic: {
      success: '#55efc4',      // Soft jade
      warning: '#fdcb6e',      // Soft amber
      error: '#d63031',        // Vermillion
      info: '#74b9ff',         // Soft sky blue
    },
  },
  typography: {
    fontFamily: {
      sans: '"Noto Sans JP", "Hiragino Sans", system-ui, -apple-system, sans-serif',
      serif: '"Noto Serif JP", "Yu Mincho", Georgia, serif',
      mono: '"Noto Sans Mono", "Osaka-Mono", monospace',
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px - zen spacing
      lg: '1.25rem',     // 20px
      xl: '1.5rem',      // 24px
      '2xl': '2rem',     // 32px
      '3xl': '2.5rem',   // 40px
      '4xl': '3rem',     // 48px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.4,        // Generous for Japanese characters
      normal: 1.75,      // Zen breathing room
      relaxed: 2,        // Maximum space (ma - negative space)
    },
  },
  spacing: {
    xs: '0.5rem',      // 8px - more generous than elegant
    sm: '0.75rem',     // 12px
    md: '1.25rem',     // 20px - zen spacing
    lg: '2rem',        // 32px
    xl: '3rem',        // 48px
    '2xl': '4rem',     // 64px
    '3xl': '6rem',     // 96px
    '4xl': '8rem',     // 128px - maximum ma (negative space)
  },
  borderRadius: {
    none: '0',         // Clean edges (wabi-sabi simplicity)
    sm: '2px',         // Minimal
    md: '4px',         // Subtle
    lg: '6px',         // Gentle
    full: '9999px',    // Reserved for special elements
  },
}

// Scandinavian Hygge Token Set
export const SCANDINAVIAN_HYGGE_TOKENS: DesignTokenSetConfig = {
  id: 'scandinavian-hygge',
  name: 'Scandinavian Hygge',
  description: 'Warm, cozy Nordic color palette',
  culturalOrigin: 'Scandinavian Design',
  colors: {
    primary: '#34495e',        // Deep slate blue
    secondary: '#7f8c8d',      // Soft gray
    accent: '#e67e22',         // Warm terracotta orange
    background: '#ecf0f1',     // Soft white (almost cream)
    surface: '#ffffff',        // Pure white
    text: {
      primary: '#2c3e50',      // Dark blue-gray
      secondary: '#7f8c8d',    // Soft gray
      disabled: '#bdc3c7',     // Light gray
    },
    semantic: {
      success: '#27ae60',      // Warm forest green
      warning: '#f39c12',      // Amber
      error: '#e74c3c',        // Warm red
      info: '#3498db',         // Sky blue
    },
  },
  typography: {
    fontFamily: {
      sans: 'Inter, "Segoe UI", system-ui, -apple-system, sans-serif',
      serif: 'Merriweather, Georgia, serif',
      mono: '"SF Mono", Menlo, monospace',
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px - slightly larger for readability
      xl: '1.375rem',    // 22px
      '2xl': '1.75rem',  // 28px
      '3xl': '2.25rem',  // 36px
      '4xl': '3rem',     // 48px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.3,        // Cozy
      normal: 1.6,       // Comfortable reading
      relaxed: 1.8,      // Maximum comfort
    },
  },
  spacing: {
    xs: '0.375rem',    // 6px
    sm: '0.625rem',    // 10px - cozy spacing
    md: '1.125rem',    // 18px
    lg: '1.75rem',     // 28px
    xl: '2.5rem',      // 40px
    '2xl': '3.5rem',   // 56px
    '3xl': '5rem',     // 80px
    '4xl': '7rem',     // 112px
  },
  borderRadius: {
    none: '0',         // For clean elements
    sm: '4px',         // Soft corners
    md: '8px',         // Welcoming
    lg: '12px',        // Cozy curves
    full: '9999px',    // Pills
  },
}

// Mediterranean Sun Token Set
export const MEDITERRANEAN_SUN_TOKENS: DesignTokenSetConfig = {
  id: 'mediterranean-sun',
  name: 'Mediterranean Sun',
  description: 'Vibrant blues, terracotta, and warm whites',
  culturalOrigin: 'Mediterranean Coastal',
  colors: {
    primary: '#0984e3',        // Azure blue (Mediterranean sea)
    secondary: '#74b9ff',      // Light sky blue
    accent: '#d63031',         // Terracotta red
    background: '#fdfbf7',     // Warm white (sun-bleached)
    surface: '#ffffff',        // Pure white
    text: {
      primary: '#2d3436',      // Charcoal (shadows)
      secondary: '#636e72',    // Warm gray
      disabled: '#b2bec3',     // Light stone
    },
    semantic: {
      success: '#00b894',      // Olive green
      warning: '#fdcb6e',      // Golden sand
      error: '#d63031',        // Terracotta
      info: '#0984e3',         // Azure
    },
  },
  typography: {
    fontFamily: {
      sans: 'Lato, "Segoe UI", system-ui, -apple-system, sans-serif',
      serif: 'Crimson Text, Georgia, serif',
      mono: 'Inconsolata, monospace',
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.25rem',     // 20px - generous
      xl: '1.5rem',      // 24px
      '2xl': '2rem',     // 32px
      '3xl': '2.75rem',  // 44px - bold headlines
      '4xl': '3.5rem',   // 56px - dramatic
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.3,        // Compact
      normal: 1.65,      // Mediterranean warmth
      relaxed: 1.9,      // Airy
    },
  },
  spacing: {
    xs: '0.375rem',    // 6px
    sm: '0.75rem',     // 12px
    md: '1.25rem',     // 20px - generous
    lg: '2rem',        // 32px
    xl: '3.25rem',     // 52px
    '2xl': '4.5rem',   // 72px
    '3xl': '6.5rem',   // 104px - dramatic
    '4xl': '9rem',     // 144px - maximum drama
  },
  borderRadius: {
    none: '0',         // Clean architecture
    sm: '3px',         // Subtle
    md: '6px',         // Inviting
    lg: '10px',        // Rounded arches
    full: '9999px',    // Circular elements
  },
}

export const DESIGN_TOKEN_SET_CONFIGS: Record<DesignTokenSet, DesignTokenSetConfig> = {
  elegant: ELEGANT_TOKENS,
  'japanese-zen': JAPANESE_ZEN_TOKENS,
  'scandinavian-hygge': SCANDINAVIAN_HYGGE_TOKENS,
  'mediterranean-sun': MEDITERRANEAN_SUN_TOKENS,
}
